/** Client preview of MANDATORY_EXPIRATION_SHARED_FILE recipient exemptions. Server remains authoritative. */

export type MandatoryExpirationExemptionRule = {
  maxAllowedByAge: number | null;
  excludedDomains: Set<string>;
  /** `''` | `'*'` | CSV of group ids */
  excludedGroups: string;
  excludedGroupsExceptions: Set<number>;
};

const DOMAIN_PATTERN =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

export function isValidExcludedDomain(domain: string): boolean {
  return DOMAIN_PATTERN.test(domain.trim().toLowerCase());
}

export function parseCsvTokens(value?: string | null): string[] {
  return (value ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
}

export function parsePositiveIntCsv(value?: string | null): Set<number> {
  const ids = new Set<number>();
  for (const token of parseCsvTokens(value)) {
    if (!/^\d+$/.test(token)) continue;
    const id = Number(token);
    if (id > 0) ids.add(id);
  }
  return ids;
}

function pickField(raw: Record<string, unknown>, ...names: string[]): unknown {
  const lower = Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [key.toLowerCase(), value]),
  );
  for (const name of names) {
    const value = lower[name.toLowerCase()];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

export function parseMandatoryExpirationAdditionalData(
  additionalData: unknown,
): MandatoryExpirationExemptionRule | null {
  if (additionalData == null || additionalData === '') return null;
  try {
    const raw =
      typeof additionalData === 'string'
        ? (JSON.parse(additionalData) as Record<string, unknown>)
        : (additionalData as Record<string, unknown>);
    if (!raw || typeof raw !== 'object') return null;

    const maxRaw = Number(pickField(raw, 'MaxAllowedByAge', 'maxallowedbyage'));
    const maxAllowedByAge = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null;

    const domains = new Set(
      parseCsvTokens(String(pickField(raw, 'ExcludedDomains', 'excludedDomains') ?? '')).map((d) =>
        d.toLowerCase(),
      ),
    );

    const excludedGroups = String(pickField(raw, 'ExcludedGroups', 'excludedGroups') ?? '').trim();

    const excludedGroupsExceptions = parsePositiveIntCsv(
      String(pickField(raw, 'ExcludedGroupsExceptions', 'excludedGroupsExceptions') ?? ''),
    );

    return {
      maxAllowedByAge,
      excludedDomains: domains,
      excludedGroups,
      excludedGroupsExceptions,
    };
  } catch {
    return null;
  }
}

export function isUserExemptFromMandatoryExpiration(
  email: string | undefined | null,
  rule: MandatoryExpirationExemptionRule,
): boolean {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@').pop()?.trim().toLowerCase();
  if (!domain) return false;
  return rule.excludedDomains.has(domain);
}

export function isGroupExemptFromMandatoryExpiration(
  groupId: number | undefined | null,
  rule: MandatoryExpirationExemptionRule,
): boolean {
  if (!groupId || groupId <= 0) return false;
  const mode = rule.excludedGroups.trim();
  if (!mode) return false;
  if (mode === '*') return !rule.excludedGroupsExceptions.has(groupId);
  return parsePositiveIntCsv(mode).has(groupId);
}

/** Conservative multi-file: exempt only if exempt under every rule. */
export function isUserExemptAcrossRules(
  email: string | undefined | null,
  rules: MandatoryExpirationExemptionRule[],
): boolean {
  if (!rules.length) return false;
  return rules.every((rule) => isUserExemptFromMandatoryExpiration(email, rule));
}

export function isGroupExemptAcrossRules(
  groupId: number | undefined | null,
  rules: MandatoryExpirationExemptionRule[],
): boolean {
  if (!rules.length) return false;
  return rules.every((rule) => isGroupExemptFromMandatoryExpiration(groupId, rule));
}

export function minMaxAllowedByAge(rules: MandatoryExpirationExemptionRule[]): number | null {
  const values = rules
    .map((rule) => rule.maxAllowedByAge)
    .filter((value): value is number => value != null && value > 0);
  return values.length ? Math.min(...values) : null;
}

/**
 * Max share expiry as .NET ticks: end of UTC day at (now + maxDays - 1), matching share form pickers.
 * `nowMs` injectable for tests.
 */
export function maxAllowedExpirationTicks(
  maxAllowedByAgeDays: number,
  nowMs: number,
  msToTicksFn: (ms: number) => number,
): number {
  const dayMs = 24 * 60 * 60 * 1000;
  const target = new Date(nowMs);
  // UTC calendar day of (now + maxDays - 1), end of that day.
  const utcMs =
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate()) +
    (maxAllowedByAgeDays - 1) * dayMs +
    (dayMs - 1);
  return msToTicksFn(utcMs);
}

/**
 * Clamp or default accessExpiration ticks under mandatory maxDays.
 * Missing / zero / past-max expiry → max allowed. Exempt callers should skip.
 */
export function clampAccessExpirationTicks(
  expiryTicks: number,
  maxAllowedByAgeDays: number,
  nowMs: number,
  msToTicksFn: (ms: number) => number,
): number {
  const maxTicks = maxAllowedExpirationTicks(maxAllowedByAgeDays, nowMs, msToTicksFn);
  if (!expiryTicks || expiryTicks <= 0 || expiryTicks > maxTicks) return maxTicks;
  return expiryTicks;
}
